import React, { useState, useEffect } from 'react';
import { OperationVariables, QueryResult, useLazyQuery } from '@apollo/client';
import {
  TagPicker,
  TagPickerList,
  TagPickerInput,
  TagPickerControl,
  TagPickerProps,
  TagPickerOption,
  TagPickerGroup,
  Tag,
  Field,
  FluentProvider,
  webLightTheme,
  useId,
} from '@fluentui/react-components';
import ICountry from '../../utils/types/ICountry';
import ITag from '../../utils/types/ITag';
import { GET_COUNTRIES_BY_SEARCH_INPUT } from '../../utils/statApi/CountryApi';
import { convertCountryToTag } from '../../utils/Helpers';

interface ICountryPickerProps {
  label?: React.ReactNode;
  itemLimit?: number;
  isDisabled: boolean;
  selectedItems: ITag[];
  errorMessage: string;
  setSelectedItems: (countryConfigCountry: ITag[]) => void;
}

const CountryPicker = ({
  setSelectedItems,
  selectedItems,
  isDisabled,
  errorMessage,
  label,
  itemLimit,
}: ICountryPickerProps): JSX.Element => {
  const [loadCountries] = useLazyQuery(GET_COUNTRIES_BY_SEARCH_INPUT);
  const [availableOptions, setAvailableOptions] = useState<ITag[]>([]);
  const [query, setQuery] = useState('');

  const errorId = useId('country-picker-error');
  // Convert selectedItems to the format expected by TagPicker v9
  const selectedOptions = (selectedItems || []).map((item) => item.key.toString());
  // Load countries when query changes
  useEffect(() => {
    if (query.length > 0) {
      loadCountries({ variables: { keyword: query } })
        .then((result: QueryResult<{ countries: ICountry[] }, OperationVariables>) => {
          const countriesList: ICountry[] = result?.data?.countries || [];
          const options = countriesList.map((country) => {
            const tag = convertCountryToTag(country);
            return {
              key: tag.key.toString(),
              name: tag.name,
            };
          });
          setAvailableOptions(options);
        })
        .catch((error) => {
          setAvailableOptions([]);
        });
    } else {
      setAvailableOptions([]);
    }
  }, [query, loadCountries]);

  const onOptionSelect: TagPickerProps['onOptionSelect'] = (e, data) => {
    if (data.value === 'no-options') {
      return;
    }

    // Check item limit
    if (itemLimit && data.selectedOptions.length > itemLimit) {
      return;
    }

    // Convert selected option keys back to full objects
    const selected = data.selectedOptions
      .map((optionKey) => {
        // Find in current selected items first
        const existing = selectedItems?.find((item) => item.key === optionKey);
        if (existing) return existing;

        // Find in available options
        const available = availableOptions.find((item) => item.key === optionKey);
        return available;
      })
      .filter(Boolean) as ITag[];

    setSelectedItems(selected);
    setQuery('');
  };

  // Filter options to exclude already selected items
  const filteredOptions = availableOptions.filter(
    (option) => !selectedOptions.includes(option.key.toString()),
  );

  const labelText = label || 'Countries';

  return (
    <FluentProvider theme={webLightTheme}>
      <Field
        label={labelText}
        required
        validationMessage={errorMessage ? { children: errorMessage, id: errorId } : undefined}
        validationState={errorMessage ? 'error' : 'none'}
      >
        <TagPicker
          onOptionSelect={onOptionSelect}
          selectedOptions={selectedOptions}
          disabled={isDisabled}
        >
          <TagPickerControl>
            <TagPickerGroup aria-label="Selected Countries">
              {(selectedItems || []).map((item) => (
                <Tag key={item.key} value={item.key.toString()}>
                  {item.name}
                </Tag>
              ))}
            </TagPickerGroup>
            <TagPickerInput
              aria-label="Search Countries"
              aria-invalid={!!errorMessage || undefined}
              aria-describedby={errorMessage ? errorId : undefined}
              placeholder="Search Countries"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </TagPickerControl>
          <TagPickerList>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <TagPickerOption value={option.key.toString()} key={option.key.toString()}>
                  {option.name}
                </TagPickerOption>
              ))
            ) : (
              <TagPickerOption value="no-options">No countries found</TagPickerOption>
            )}
          </TagPickerList>
        </TagPicker>
      </Field>
    </FluentProvider>
  );
};
export default CountryPicker;
